import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useAudioTransport } from './useAudioTransport';

/**
 * ブラウジングプレビュー用の試聴（オーディション）フック。
 *
 * ファイル一覧の行群を「オーディション領域」として扱い、
 * - 初回クリックで行選択（roving tabindex でフォーカス移動）
 * - ↑↓ キーで行移動（再生中なら移動先を続けて再生 = 連続試聴）
 * - Space キーで選択行の再生 / 停止
 * を提供する。再生エンジンはリストごとに 1 つの useAudioTransport を共有するため、
 * リスト内の同時再生は構造的に起きない（アプリ全体の排他は playbackArbiter が担う）。
 */

export interface AuditionItem {
  /** 行の一意キー（ファイル ID 等） */
  key: string;
  /** 再生 URL */
  src: string;
}

/** 行要素へ spread する props（roving tabindex + キーボード操作） */
export interface AuditionRowProps {
  ref: (_el: HTMLElement | null) => void;
  tabIndex: number;
  onClick: () => void;
  onKeyDown: (_e: React.KeyboardEvent<HTMLElement>) => void;
  style: React.CSSProperties;
}

export interface AudioAudition {
  /** 選択中の行キー */
  selectedKey: string | null;
  /** 再生中の行キー（停止中は null） */
  playingKey: string | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  /** 行を選択する（フォーカス移動込み） */
  select: (_key: string) => void;
  /** 再生 / 停止を切り替える。未選択の行キーを渡すと選択して再生する */
  toggle: (_key?: string) => void;
  pause: () => void;
  /** 絶対シーク（秒）。波形インスペクタなど外部 UI からの頭出し用 */
  seekTo: (_seconds: number) => void;
  /** 前 / 次のオーディオへ選択を移す（再生中なら移動先も続けて再生する） */
  selectPrev: () => void;
  selectNext: () => void;
  /**
   * この音源をスペースキー再生のターゲットにする。
   * 一覧の外（波形インスペクタ等）にフォーカスがあってもスペースで再生できる
   */
  markActive: () => void;
  /** 行要素へ spread する props を返す */
  getRowProps: (_key: string) => AuditionRowProps;
}

/** 行の内側にある操作要素（再生ボタン等）上のキー入力は試聴操作として扱わない */
function isInteractiveTarget(target: EventTarget | null, row: HTMLElement): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const interactive = target.closest(
    'button, a, input, textarea, select, [role="button"], [contenteditable="true"]',
  );
  // 行そのものは除外する。dnd-kit の useDraggable が role="button" を付けるため、
  // 行にフォーカスがあるだけで「ボタン操作中」と誤判定してしまう
  return !!interactive && interactive !== row && row.contains(interactive);
}

export function useAudioAudition(items: AuditionItem[]): AudioAudition {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const rowElsRef = useRef(new Map<string, HTMLElement>());
  // 選択切替後に自動再生するか（再生中の行移動・別行の再生ボタン押下時）
  const autoPlayRef = useRef(false);

  const selectedItem = items.find(i => i.key === selectedKey) ?? null;

  const transport = useAudioTransport({
    src: selectedItem?.src,
    preload: 'none',
  });

  // 選択が変わったら（src 差し替え後に）自動再生フラグを消化する
  useEffect(() => {
    if (!selectedItem) return;
    if (autoPlayRef.current) {
      autoPlayRef.current = false;
      transport.play();
    }
    // transport は毎レンダー新しい参照になるため selectedItem の変化のみ追う
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.key]);

  const focusRow = useCallback((key: string) => {
    const el = rowElsRef.current.get(key);
    if (el) {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest' });
    }
  }, []);

  const select = useCallback((key: string) => {
    setSelectedKey(prev => (prev === key ? prev : key));
    focusRow(key);
  }, [focusRow]);

  const toggle = useCallback((key?: string) => {
    // onClick へ直接渡されるとクリックイベントが key に入り、存在しないキーで
    // 選択が飛ぶ。文字列以外は「キー指定なし」として扱う
    const targetKey = typeof key === 'string' ? key : selectedKey;
    if (!targetKey) return;
    if (targetKey !== selectedKey) {
      autoPlayRef.current = true;
      select(targetKey);
      return;
    }
    transport.toggle();
  }, [selectedKey, select, transport]);

  const moveSelection = useCallback((delta: 1 | -1) => {
    if (items.length === 0) return;
    const currentIndex = items.findIndex(i => i.key === selectedKey);
    const nextIndex = currentIndex < 0
      ? (delta > 0 ? 0 : items.length - 1)
      : Math.max(0, Math.min(items.length - 1, currentIndex + delta));
    const next = items[nextIndex];
    if (!next || next.key === selectedKey) return;
    // 再生中の行移動は移動先を続けて再生（連続試聴）
    if (transport.isPlaying) {
      autoPlayRef.current = true;
    }
    select(next.key);
  }, [items, selectedKey, select, transport.isPlaying]);

  const selectPrev = useCallback(() => moveSelection(-1), [moveSelection]);
  const selectNext = useCallback(() => moveSelection(1), [moveSelection]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (isInteractiveTarget(e.target, e.currentTarget)) return;
    if (e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveSelection(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(-1);
    }
  }, [toggle, moveSelection]);

  const getRowProps = useCallback((key: string): AuditionRowProps => ({
    ref: (el: HTMLElement | null) => {
      if (el) {
        rowElsRef.current.set(key, el);
      } else {
        rowElsRef.current.delete(key);
      }
    },
    tabIndex: selectedKey === key ? 0 : -1,
    onClick: () => select(key),
    onKeyDown: handleKeyDown,
    // 選択表示は背景色で行うためフォーカスリングは消す
    style: { outline: 'none' },
  }), [selectedKey, select, handleKeyDown]);

  return {
    selectedKey,
    playingKey: transport.isPlaying ? selectedKey : null,
    isPlaying: transport.isPlaying,
    currentTime: transport.currentTime,
    duration: transport.duration,
    select,
    toggle,
    pause: transport.pause,
    seekTo: transport.seekTo,
    selectPrev,
    selectNext,
    markActive: transport.markActive,
    getRowProps,
  };
}
